import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import Seo from "@/components/Seo";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

// Build API base robustly so `REACT_APP_BACKEND_URL` can include or omit the trailing `/api`.
const _RAW_BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const _BACKEND_BASE = _RAW_BACKEND.replace(/\/+$/, "");
const API = _BACKEND_BASE.endsWith("/api") ? _BACKEND_BASE : `${_BACKEND_BASE}/api`;

const PROPERTY_TYPES = [
  { value: "kuca", label: "Obiteljska kuća" },
  { value: "stan", label: "Stan / poslovni prostor" },
  { value: "pomocna_zgrada", label: "Pomoćna zgrada (garaža, spremište)" },
  { value: "gospodarska_zgrada", label: "Gospodarska / poljoprivredna zgrada" },
  { value: "drugo", label: "Drugo" },
];

const empty = {
  full_name: "",
  email: "",
  phone: "",
  service_type: "legalizacija",
  client_address: "",
  client_oib: "",
  property_address: "",
  city: "",
  property_type: "",
  year_built: "",
  area_m2: "",
  cadastral_municipality: "",
  cadastral_parcel: "",
  description: "",
};

export default function PrijavaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const vrsta = searchParams.get("vrsta");
    const valid = PROPERTY_TYPES.map((t) => t.value);
    if (vrsta && valid.includes(vrsta)) {
      setForm((f) => ({ ...f, property_type: vrsta }));
    }
  }, [searchParams]);

  const set = (k) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Unesite ime i prezime";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Neispravna e-mail adresa";
    if (form.phone.trim().length < 5) e.phone = "Unesite telefon";
    if (form.client_oib.trim() && !/^\d{11}$/.test(form.client_oib.trim())) e.client_oib = "OIB se sastoji od točno 11 znamenki";
    if (form.property_address.trim().length < 2) e.property_address = "Unesite adresu objekta";
    if (form.city.trim().length < 2) e.city = "Unesite mjesto";
    if (!form.property_type) e.property_type = "Odaberite vrstu objekta";
    if (form.description.trim().length < 10) e.description = "Kratko opišite objekt (min. 10 znakova)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Provjerite obavezna polja u formi.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, form);
      setDone(true);
      toast.success("Prijava uspješno poslana!");
      setForm(empty);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Slanje nije uspjelo. Pokušajte ponovno.";
      toast.error(typeof msg === "string" ? msg : "Slanje nije uspjelo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-12 py-28 text-center" data-testid="prijava-success">
        <Seo
          title="Prijava zaprimljena"
          description="Vaša prijava je uspješno zaprimljena. Naš tim će vas kontaktirati u roku od 24 sata."
          path="/prijava"
          robots="noindex,nofollow"
        />
        <CheckCircle2 className="w-14 h-14 text-ink mx-auto" strokeWidth={1.25} />
        <div className="overline text-ink-soft mt-6">Prijava zaprimljena</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold text-ink mt-4 leading-tight">
          Hvala! Javljamo se u roku<br />od 24 sata.
        </h1>
        <p className="text-ink-soft mt-6 leading-relaxed max-w-xl mx-auto">
          Vaša prijava je uspješno zaprimljena. Naš tim će pregledati podatke i
          uputiti vas u prve korake postupka legalizacije.
        </p>
        <div className="mt-10 flex justify-center gap-3 flex-wrap">
          <button
            onClick={() => { setDone(false); }}
            className="border border-ink text-ink px-6 py-3 text-sm font-medium rounded-sm hover:bg-secondary transition-colors"
            data-testid="prijava-new-btn"
          >
            Nova prijava
          </button>
          <button
            onClick={() => navigate("/")}
            className="bg-ink text-white px-6 py-3 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors inline-flex items-center gap-2"
            data-testid="prijava-home-btn"
          >
            Natrag na naslovnu <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="prijava-page">
      <Seo
        title="Prijava za legalizaciju"
        description="Pošaljite prijavu za legalizaciju objekta u Dalmaciji. Ispunite osnovne podatke i naš tim će vas kontaktirati s procjenom i sljedećim koracima."
        path="/prijava"
      />
      <section className="border-b border-hair">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-12 lg:pt-24 lg:pb-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="overline text-ink-soft flex items-center gap-3">
                <span className="w-8 h-px bg-ink-soft/40" /> Prijava
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-bold text-ink mt-6">
                Forma za<br />prijavu klijenata.
              </h1>
            </div>
            <div className="md:col-span-4 text-ink-soft leading-relaxed">
              Ispunite osnovne podatke o sebi i objektu. Sve podatke koristimo
              isključivo za potrebe procjene postupka legalizacije.
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-12 gap-10" data-testid="prijava-form">
          {/* SIDE */}
          <aside className="md:col-span-4">
            <div className="md:sticky md:top-24 border border-hair p-6 bg-white">
              <ShieldCheck className="w-5 h-5 text-ink" strokeWidth={1.5} />
              <div className="overline text-ink-soft mt-4">Privatnost</div>
              <p className="text-sm text-ink-soft mt-2 leading-relaxed">
                Vaši podaci pohranjuju se sukladno GDPR-u i koriste isključivo za
                potrebe obrade vaše prijave.
              </p>
              <div className="h-px bg-hair my-6" />
              <div className="overline text-ink-soft">Pomoć</div>
              <p className="text-sm text-ink-soft mt-2 leading-relaxed">
                Nemate sve podatke o objektu? Ostavite ono što imate — kontaktirat
                ćemo vas i pomoći s ostatkom.
              </p>
            </div>
          </aside>

          {/* FORM */}
          <div className="md:col-span-8 space-y-10">
            <FieldSet title="Kontakt podaci" n="01">
              <Field label="Ime i prezime *" error={errors.full_name}>
                <Input
                  data-testid="input-full-name"
                  value={form.full_name}
                  onChange={set("full_name")}
                  placeholder="Upišite vaše ime i prezime"
                  className="rounded-sm"
                />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="E-mail *" error={errors.email}>
                  <Input
                    type="email"
                    data-testid="input-email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="Upišite vaš e-mail"
                    className="rounded-sm"
                  />
                </Field>
                <Field label="Telefon *" error={errors.phone}>
                  <Input
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Upišite vaš broj telefona"
                    className="rounded-sm"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Adresa stanovanja (opcionalno)" error={errors.client_address}>
                  <Input
                    data-testid="input-client-address"
                    value={form.client_address}
                    onChange={set("client_address")}
                    placeholder="Upišite vašu adresu stanovanja (ulica, broj, mjesto)"
                    className="rounded-sm"
                  />
                </Field>
                <Field label="OIB (opcionalno)" error={errors.client_oib}>
                  <Input
                    data-testid="input-client-oib"
                    value={form.client_oib}
                    onChange={set("client_oib")}
                    placeholder="Upišite vaš OIB (11 znamenki)"
                    inputMode="numeric"
                    maxLength={11}
                    className="rounded-sm"
                  />
                </Field>
              </div>
            </FieldSet>

            <FieldSet title="Podaci o objektu" n="02">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Adresa objekta *" error={errors.property_address}>
                  <Input
                    data-testid="input-address"
                    value={form.property_address}
                    onChange={set("property_address")}
                    placeholder="Upišite ulicu i kućni broj"
                    className="rounded-sm"
                  />
                </Field>
                <Field label="Mjesto *" error={errors.city}>
                  <Input
                    data-testid="input-city"
                    value={form.city}
                    onChange={set("city")}
                    placeholder="Upišite mjesto / grad"
                    className="rounded-sm"
                  />
                </Field>
              </div>
              <Field label="Vrsta objekta *" error={errors.property_type}>
                <Select value={form.property_type} onValueChange={set("property_type")}>
                  <SelectTrigger className="rounded-sm h-11" data-testid="select-type">
                    <SelectValue placeholder="Odaberite vrstu objekta" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value} data-testid={`option-${t.value}`}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Godina izgradnje (procjena)">
                  <Input
                    data-testid="input-year"
                    value={form.year_built}
                    onChange={set("year_built")}
                    placeholder="Upišite godinu (npr. 1998)"
                    className="rounded-sm"
                  />
                </Field>
                <Field label="Površina objekta (m²)">
                  <Input
                    data-testid="input-area"
                    value={form.area_m2}
                    onChange={set("area_m2")}
                    placeholder="Upišite površinu u m² (npr. 120)"
                    className="rounded-sm"
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Katastarska općina (k.o.)">
                  <Input
                    data-testid="input-cadastral-municipality"
                    value={form.cadastral_municipality}
                    onChange={set("cadastral_municipality")}
                    placeholder="Upišite k.o. (npr. Split)"
                    className="rounded-sm"
                  />
                </Field>
                <Field label="Katastarska čestica (k.č.)">
                  <Input
                    data-testid="input-cadastral-parcel"
                    value={form.cadastral_parcel}
                    onChange={set("cadastral_parcel")}
                    placeholder="Upišite broj čestice (npr. 1234/5)"
                    className="rounded-sm"
                  />
                </Field>
              </div>
            </FieldSet>

            <FieldSet title="Opis i napomene" n="03">
              <Field label="Opis objekta i razloga prijave *" error={errors.description}>
                <Textarea
                  data-testid="input-description"
                  value={form.description}
                  onChange={set("description")}
                  rows={5}
                  placeholder="Upišite kratak opis objekta — eventualne dogradnje, postojeću dokumentaciju i ostale relevantne informacije..."
                  className="rounded-sm"
                />
              </Field>
            </FieldSet>

            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between pt-4 border-t border-hair">
              <p className="text-xs text-ink-soft max-w-md leading-relaxed">
                Slanjem prijave potvrđujete da ste suglasni s obradom osobnih
                podataka u svrhu obrade upita.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="prijava-submit"
                className="inline-flex items-center gap-2 bg-ink text-white px-8 py-3.5 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Slanje...</>
                ) : (
                  <>Prijavi se <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} /></>
                )}
              </button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}

function FieldSet({ title, n, children }) {
  return (
    <div className="border border-hair bg-white p-6 lg:p-8">
      <div className="flex items-baseline justify-between mb-6">
        <div className="overline text-ink-soft">{title}</div>
        <span className="font-display text-3xl text-ink/15 font-bold leading-none">{n}</span>
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.12em] text-ink-soft font-semibold">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
      {error && <p className="text-xs mt-1.5 text-[#D92D20]">{error}</p>}
    </div>
  );
}
